/**
 * API Helper Functions
 *
 * Utilities for working with backend ApiResponse format
 */

/**
 * Extract data from backend ApiResponse
 * @param {Object} response - Axios response object
 * @returns {any} The data from response.data.data
 */
export const extractData = (response) => {
  if (response.data && response.data.success) {
    return response.data.data;
  }
  return null;
};

/**
 * Extract message from backend ApiResponse
 * @param {Object} response - Axios response object
 * @returns {string} The message from response.data.message
 */
export const extractMessage = (response) => {
  return response.data?.message || '';
};

/**
 * Check if response was successful
 * @param {Object} response - Axios response object
 * @returns {boolean} True if success
 */
export const isSuccessResponse = (response) => {
  return response.data?.success === true;
};

/**
 * Format backend error for display
 * @param {Object} error - Error object from API
 * @returns {string} User-friendly error message
 */
export const formatErrorMessage = (error) => {
  if (error.message) {
    return error.message;
  }

  switch (error.status) {
    case 400:
      return 'Invalid request. Please check your input.';
    case 401:
      return 'Please log in to continue.';
    case 403:
      return 'You do not have permission to perform this action.';
    case 404:
      return 'The requested resource was not found.';
    case 409:
      return 'This resource already exists.';
    case 500:
      return 'Server error. Please try again later.';
    default:
      return 'An unexpected error occurred.';
  }
};

/**
 * Format price from cents to pounds with currency symbol
 * All backend prices are now in cents (integers)
 *
 * @param {number} priceInCents - Price in cents (e.g., 8999 = £89.99)
 * @param {string} currency - Currency symbol (default: £)
 * @returns {string} Formatted price string
 */
export const formatPrice = (priceInCents, currency = '£') => {
  if (priceInCents === null || priceInCents === undefined) {
    return `${currency}0.00`;
  }

  const priceInPounds = priceInCents / 100;
  return `${currency}${priceInPounds.toFixed(2)}`;
};

/**
 * Format price that is already in pounds (for orders)
 * Order totals from the API are returned in pounds (decimal), not cents
 * @param {number} priceInPounds - Price in pounds (decimal)
 * @param {string} currency - Currency symbol (default: £)
 * @returns {string} Formatted price string
 */
export const formatOrderPrice = (priceInPounds, currency = '£') => {
  if (priceInPounds === null || priceInPounds === undefined) {
    return `${currency}0.00`;
  }

  return `${currency}${Number(priceInPounds).toFixed(2)}`;
};

/**
 * Default placeholder image path
 */
export const PLACEHOLDER_IMAGE = '/assets/img/placeholder.svg';

/**
 * Check if a URL is valid (basic validation)
 * @param {string} url - URL to validate
 * @returns {boolean} True if URL appears valid
 */
export const isValidImageUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  // Check for empty strings, undefined-like values
  if (url.trim() === '' || url === 'undefined' || url === 'null') return false;
  // Basic URL pattern check - must start with http, https, or /
  return url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/');
};

/**
 * Get the primary image URL from a product's images array
 * @param {Object} product - Product object with images array
 * @param {string} fallback - Fallback image URL
 * @returns {string} Primary image URL or fallback
 */
export const getPrimaryImage = (product, fallback = PLACEHOLDER_IMAGE) => {
  if (!product || !product.images || product.images.length === 0) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[getPrimaryImage] No images for product:', product?.name, '- using placeholder');
    }
    return fallback;
  }
  const primary = product.images.find(img => img.isPrimary);
  const url = primary?.url || product.images[0]?.url;
  const result = isValidImageUrl(url) ? url : fallback;

  if (process.env.NODE_ENV === 'development' && result === fallback) {
    console.log('[getPrimaryImage] Invalid image URL for product:', product?.name, 'URL:', url);
  }

  return result;
};

/**
 * Get all product images from API response
 * @param {Object} product - Product object with images array
 * @param {string} fallback - Fallback image URL
 * @returns {string[]} Array of image URLs
 */
export const getProductImages = (product, fallback = PLACEHOLDER_IMAGE) => {
  if (!product || !product.images || product.images.length === 0) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[getProductImages] No images for product:', product?.name, product?.images);
    }
    return [fallback];
  }
  const validUrls = product.images
    .map(img => img.url)
    .filter(url => isValidImageUrl(url));

  if (process.env.NODE_ENV === 'development' && validUrls.length === 0) {
    console.log('[getProductImages] No valid image URLs for product:', product?.name, 'Raw images:', product.images);
  }

  return validUrls.length > 0 ? validUrls : [fallback];
};

/**
 * Handle image load error - sets fallback image
 * @param {Event} e - Error event from img element
 */
export const handleImageError = (e) => {
  // Prevent infinite loop - check if already using placeholder
  if (!e.target.src.includes('placeholder.svg')) {
    e.target.src = PLACEHOLDER_IMAGE;
  }
};

/**
 * Check if a product is in stock
 * @param {Object} product - Product object
 * @returns {boolean} True if in stock
 */
export const isInStock = (product) => {
  if (!product) return false;
  // Check stockStatus first (backend renamed from 'status' to 'stockStatus')
  if (product.stockStatus === 'out_of_stock') return false;
  if (product.stockStatus === 'in_stock') return true;
  // Fallback to checking stock quantity
  return product.stock > 0;
};

/**
 * Low stock threshold constant
 * Products with stock <= this value show "Only X left!" message
 */
export const LOW_STOCK_THRESHOLD = 10;

/**
 * Get stock display information for a product
 * Returns text, CSS class, and whether the product can be added to cart
 *
 * @param {number} stock - Current stock quantity
 * @param {string} stockStatus - Stock status ('in_stock' or 'out_of_stock')
 * @returns {Object} { text, className, canAddToCart }
 */
export const getStockDisplay = (stock, stockStatus) => {
  // Out of stock
  if (stockStatus === 'out_of_stock' || stock === 0 || stock === null || stock === undefined) {
    return {
      text: 'Out of Stock',
      className: 'out-of-stock',
      canAddToCart: false
    };
  }

  // Low stock (1-10 items)
  if (stock <= LOW_STOCK_THRESHOLD) {
    return {
      text: `Only ${stock} left in stock!`,
      className: 'low-stock',
      canAddToCart: true
    };
  }

  // In stock (11+ items)
  return {
    text: 'In Stock',
    className: 'in-stock',
    canAddToCart: true
  };
};

/**
 * Add cache-busting parameter to image URL
 * Prevents browser from serving stale cached images after WebSocket updates
 *
 * @param {string} url - Image URL
 * @param {number|string} cacheKey - Cache-busting key (timestamp or version number)
 * @returns {string} URL with cache-busting parameter
 */
export const addCacheBuster = (url, cacheKey) => {
  if (!url || typeof url !== 'string') return url;
  if (!cacheKey) return url;

  // Don't add cache buster to placeholder or data URLs
  if (url.includes('placeholder') || url.startsWith('data:')) return url;

  // Check if URL already has query parameters
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}v=${cacheKey}`;
};
